/**
 * Projects by Stage Chart Component
 * Phase 6 Batch 3 - Core 4 Reports
 *
 * Pie/Donut chart showing project distribution by workflow stage
 */
import { useProjectsByStage } from '@/hooks/useDashboardReports';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Consistent color palette matching other charts and brand colors
export const CHART_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444', '#ec4899', '#06b6d4'];
const COLORS = CHART_COLORS;

export function ProjectsStageChart() {
  const { data, isLoading, error } = useProjectsByStage();
  const navigate = useNavigate();

  // Handle pie slice click to navigate to filtered projects
  const handlePieClick = (data: any) => {
    if (data?.stage) {
      navigate(`/projects?stage=${data.stage}`);
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="h-4 bg-gray-200 rounded w-1/3 mb-4 animate-pulse"></div>
        <div className="h-64 bg-gray-100 rounded animate-pulse"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        <AlertCircle className="inline mr-2" size={20} />
        Failed to load projects data
      </div>
    );
  }

  const chartData = data?.data || [];
  const total = data?.total || 0;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const CustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }: any) => {
    const RADIAN = Math.PI / 180;
    // Position label just outside the pie
    const radius = outerRadius + 25;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    if (percent < 0.05) return null; // Don't show labels for small slices

    return (
      <text
        x={x}
        y={y}
        fill="#374151"
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        fontSize={13}
        fontWeight="600"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow" data-testid="projects-stage-chart">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">
        Projects by Workflow Stage
      </h2>
      {chartData.length > 0 ? (
        <>
          <ResponsiveContainer width="100%" height={320}>
            <PieChart margin={{ top: 10, right: 30, left: 30, bottom: 10 }}>
              <Pie
                data={chartData}
                cx="50%"
                cy="45%"
                labelLine={false}
                label={CustomLabel}
                outerRadius={90}
                fill="#8884d8"
                dataKey="count"
                nameKey="stage_label"
                paddingAngle={2}
                onClick={handlePieClick}
                className="cursor-pointer"
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} className="cursor-pointer" />
                ))}
              </Pie>
              <Tooltip
                formatter={(value: number, name: string, props: any) => [
                  `${value} projects (${props.payload.percentage}%)`,
                  name
                ]}
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                }}
              />
              <Legend
                verticalAlign="bottom"
                height={36}
                formatter={(value, entry: any) => `${value} (${entry.payload.count})`}
                iconType="circle"
                wrapperStyle={{ paddingTop: '10px' }}
              />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-4 border-t pt-4">
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div
                className="cursor-pointer hover:bg-gray-50 rounded p-1 -m-1 transition-colors"
                onClick={() => navigate('/projects')}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && navigate('/projects')}
                data-testid="total-projects-link"
              >
                <span className="text-gray-600">Total Projects:</span>
                <span className="ml-2 font-semibold text-sky-600 underline">{total}</span>
              </div>
              <div
                className="cursor-pointer hover:bg-purple-50 rounded p-1 -m-1 transition-colors"
                onClick={() => navigate('/billable-items?status=ready')}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && navigate('/billable-items?status=ready')}
                data-testid="unbilled-value-link"
              >
                <span className="text-gray-600">Unbilled Value:</span>
                <span className="ml-2 font-semibold text-slate-600 underline">
                  {formatCurrency(chartData.reduce((sum, stage) => sum + stage.unbilled_value, 0))}
                </span>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="text-center py-12 text-gray-500">
          No active projects to display
        </div>
      )}
    </div>
  );
}
